import {
  Box,
  Button,
  ButtonProps,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import { ThreadActivityDataProposal } from '@rolebase/shared/model/proposal'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { CopyIcon } from 'src/icons'
import { ThreadContext } from '@/thread/contexts/ThreadContext'
import { useContext } from 'react'
import useCanPropose from '../hooks/useCanPropose'
import ProposalModal from '../modals/ProposalModal'

interface Props extends ButtonProps {
  threadId: string
  data: ThreadActivityDataProposal
}

// Open a fresh, editable proposal prefilled from an existing one. Shared by the
// "cancel and recreate" menu action and the recreate button on refused cards.
export default function ProposalRecreateButton({
  threadId,
  data,
  ...buttonProps
}: Props) {
  const { t } = useTranslation()
  const modal = useDisclosure()
  // Re-creating is creating: same rule, one propose where one votes — and the
  // level of the tension refuses it just the same (GOUV-16, CONF-02).
  const { circle, thread } = useContext(ThreadContext) ?? {}
  const { canPropose, refusal, decidingCircleNames } = useCanPropose(
    circle?.id,
    thread
  )

  return (
    <>
      {/* A greyed button that says nothing reads as a breakage. Same two
          messages as the action bar: the refusals ask the same things here. */}
      <Tooltip
        label={
          refusal
            ? t(
                refusal === 'roleLevel'
                  ? 'ThreadActivityCreate.proposalRefusedRoleLevel'
                  : 'ThreadActivityCreate.proposalRefused',
                {
                  circles: decidingCircleNames.join(' · '),
                  count: decidingCircleNames.length,
                }
              )
            : undefined
        }
        hasArrow
        isDisabled={canPropose}
      >
        <Box display="inline-block">
          <Button
            size="sm"
            variant="outline"
            leftIcon={<CopyIcon size={18} />}
            onClick={modal.onOpen}
            isDisabled={!canPropose}
            {...buttonProps}
          >
            {t('ThreadActivityProposal.recreate')}
          </Button>
        </Box>
      </Tooltip>

      {modal.isOpen && (
        <ProposalModal
          isOpen
          threadId={threadId}
          defaults={data}
          onClose={modal.onClose}
        />
      )}
    </>
  )
}
